import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { studentAPI, narrativeAPI, classroomAPI, narrativeProgressAPI, readingBeeAPI } from '@/lib/api';
import { Moon, BookOpen, Plus, TrendingUp, Clock, BookMarked, Users, ArrowRight, HelpCircle, RotateCcw, WifiOff, Award, Music, Video, BarChart3, X, CheckCircle, Circle, Brain, Eye, Printer, Trophy, Target, Archive, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import StoryGenerationDialog from '@/components/student/StoryGenerationDialog';
import NarrativeReader from '@/components/student/NarrativeReader';
import OnboardingWizard from '@/components/OnboardingWizard';
import { studentOnboardingSteps } from '@/components/onboardingSteps';
import FAQSection from '@/components/FAQSection';
import { studentFAQ } from '@/components/faqContent';
import OfflineLibrary from '@/components/OfflineLibrary';
import SpellingBee from '@/components/student/SpellingBee';
import TaskReminders from '@/components/student/TaskReminders';
import StudentMediaLibrary from '@/components/student/StudentMediaLibrary';
import StudentReadingBee from '@/components/student/StudentReadingBee';
import StudentBedtimeShelf from '@/components/student/StudentBedtimeShelf';
import WritingStudio from '@/components/student/WritingStudio';

const C = {
  bg: '#0A0F1E', card: '#1A2236', surface: '#111827',
  gold: '#D4A853', goldLight: '#F5D799', teal: '#38BDF8',
  cream: '#F8F5EE', muted: '#94A3B8',
};

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="p-4 sm:p-5 rounded-2xl" style={{ background: C.card, border: '1px solid rgba(255,255,255,0.08)' }}>
    <div className="flex items-center gap-3 sm:gap-4">
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${accent}18` }}>
        <Icon size={20} style={{ color: accent }} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: C.muted }}>{label}</p>
        <p className="text-2xl sm:text-3xl font-bold mt-0.5" style={{ color: C.cream }}>{value}</p>
        {sub && <p className="text-xs mt-1 truncate" style={{ color: C.muted }}>{sub}</p>}
      </div>
    </div>
  </div>
);

const formatStatsTime = (seconds) => {
  if (!seconds) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

// Announces active Reading Bees at the top of the student home.
// Shows nothing when there are no open bees. Clicking jumps the student
// into the Reading Bee section via the onOpen callback.
const ReadingBeeBanner = ({ onOpen }) => {
  const { data: bees = [] } = useQuery({
    queryKey: ['student-home-reading-bees'],
    queryFn: async () => {
      try { return (await readingBeeAPI.getAll()).data || []; }
      catch { return []; }
    },
    refetchInterval: 60000,
  });

  const activeBees = (bees || []).filter(
    (b) => b.status === 'open' || b.status === 'voting'
  );
  if (activeBees.length === 0) return null;

  return (
    <div
      className="mb-5 rounded-2xl p-4 sm:p-5 cursor-pointer transition-transform hover:scale-[1.01]"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen(); }}
      style={{
        background: 'linear-gradient(135deg, rgba(212,168,83,0.18) 0%, rgba(212,168,83,0.08) 100%)',
        border: `2px solid ${C.gold}`,
        boxShadow: '0 4px 24px rgba(212,168,83,0.25)',
      }}
      data-testid="reading-bee-banner"
    >
      <div className="flex items-center gap-4">
        <div
          className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center flex-shrink-0 text-3xl sm:text-4xl"
          style={{ background: C.gold }}
          aria-hidden
        >
          {activeBees[0].cover_emoji || '🏆'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-0.5" style={{ color: C.goldLight }}>
            Reading Bee {activeBees.length > 1 ? `(${activeBees.length} open)` : ''}
          </p>
          <h3 className="text-lg sm:text-2xl font-bold truncate" style={{ color: C.cream }}>
            {activeBees[0].title}
          </h3>
          <p className="text-xs sm:text-sm truncate" style={{ color: C.muted }}>
            {activeBees[0].narrative_title
              ? `Read "${activeBees[0].narrative_title}" aloud and enter!`
              : 'Enter now!'}
            {activeBees[0].prize_description ? ` · Prize: ${activeBees[0].prize_description}` : ''}
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-1 font-bold text-sm" style={{ color: C.gold }}>
          Enter <ArrowRight size={18} />
        </div>
      </div>
    </div>
  );
};

const StoryStatsModal = ({ narrativeId, onClose }) => {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['narrative-stats', narrativeId],
    queryFn: async () => (await narrativeProgressAPI.getStats(narrativeId)).data,
    enabled: !!narrativeId,
  });

  if (!narrativeId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-2xl overflow-hidden" style={{ background: C.card, border: `2px solid ${C.gold}` }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div className="flex items-center gap-2">
            <BarChart3 size={20} style={{ color: C.gold }} />
            <h3 className="text-lg font-bold" style={{ color: C.cream }}>{stats?.title || 'Story Stats'}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => {
              const token = localStorage.getItem('token');
              const url = narrativeProgressAPI.getReportUrl(narrativeId);
              fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                .then(res => res.text())
                .then(html => { const w = window.open('', '_blank'); w.document.write(html); w.document.close(); });
            }} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="Print Story Report">
              <Printer size={18} style={{ color: C.gold }} />
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
              <X size={18} style={{ color: C.muted }} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoading ? (
            <p className="text-center py-8 font-bold" style={{ color: C.muted }}>Loading your stats...</p>
          ) : stats ? (
            <>
              {/* Overall Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl" style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)' }}>
                  <p className="text-[10px] font-bold uppercase" style={{ color: C.muted }}>Completion</p>
                  <p className="text-2xl font-bold" style={{ color: '#818CF8' }}>{stats.progress.completion_percentage}%</p>
                  <p className="text-xs" style={{ color: C.muted }}>{stats.progress.chapters_completed}/{stats.progress.chapters_total} chapters</p>
                </div>
                <div className="p-3 rounded-xl" style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.25)' }}>
                  <p className="text-[10px] font-bold uppercase" style={{ color: C.muted }}>Vocab Mastered</p>
                  <p className="text-2xl font-bold" style={{ color: '#34D399' }}>{stats.vocabulary.tokens_mastered_count}</p>
                  <p className="text-xs" style={{ color: C.muted }}>of {stats.vocabulary.tokens_encountered_count} words ({stats.vocabulary.mastery_rate}%)</p>
                </div>
                <div className="p-3 rounded-xl" style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)' }}>
                  <p className="text-[10px] font-bold uppercase" style={{ color: C.muted }}>Reading Time</p>
                  <p className="text-2xl font-bold" style={{ color: '#FBBF24' }}>{formatStatsTime(stats.reading_stats.total_reading_seconds)}</p>
                  <p className="text-xs" style={{ color: C.muted }}>{stats.reading_stats.sessions_count} sessions</p>
                </div>
                <div className="p-3 rounded-xl" style={{ background: 'rgba(244,114,182,0.1)', border: '1px solid rgba(244,114,182,0.25)' }}>
                  <p className="text-[10px] font-bold uppercase" style={{ color: C.muted }}>Reading Speed</p>
                  <p className="text-2xl font-bold" style={{ color: '#F472B6' }}>{stats.reading_stats.average_wpm || '—'}</p>
                  <p className="text-xs" style={{ color: C.muted }}>words per minute</p>
                </div>
              </div>

              {/* Assessment Score */}
              {stats.assessments.completed > 0 && (
                <div className="p-3 rounded-xl" style={{ background: 'rgba(212,168,83,0.08)', border: '1px solid rgba(212,168,83,0.2)' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase" style={{ color: C.muted }}>Assessment Score</p>
                      <p className="text-xl font-bold" style={{ color: C.gold }}>{stats.assessments.average_accuracy}% accuracy</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs" style={{ color: C.muted }}>{stats.assessments.total_correct}/{stats.assessments.total_questions_answered} correct</p>
                      <p className="text-xs" style={{ color: C.muted }}>{stats.assessments.completed} assessment{stats.assessments.completed !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Story cost — populated by the backend from cost_logs filtered
                  by this narrative_id. Tells parents what this story actually
                  costs to generate + voice-read. Subsequent listens hit cached
                  mp3 files so the number only goes up when fresh audio is
                  rendered (new chapter, new voice, etc). */}
              {stats.spend && stats.spend.total_cost > 0 && (
                <div className="p-3 rounded-xl" style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.25)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold uppercase" style={{ color: C.muted }}>Cost of this story</p>
                    <p className="text-xl font-bold" style={{ color: '#38BDF8' }}>${stats.spend.total_cost.toFixed(4)}</p>
                  </div>
                  {stats.spend.by_type?.length > 0 && (
                    <div className="space-y-1">
                      {stats.spend.by_type.map((row) => (
                        <div key={row.type} className="flex items-center justify-between text-[11px]">
                          <span style={{ color: C.cream }}>
                            {{
                              story_generation: 'Story writing',
                              tts_elevenlabs: 'Voice (ElevenLabs)',
                              tts_openai: 'Voice (OpenAI)',
                              assessment_evaluation: 'Assessment grading',
                              voice_cloning: 'Voice cloning',
                              virtual_background_prompts: 'Background prompts',
                            }[row.type] || row.type}
                            <span className="ml-1" style={{ color: C.muted }}>× {row.count}</span>
                          </span>
                          <span style={{ color: '#38BDF8' }}>${row.total_cost.toFixed(4)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {stats.spend.tts_characters > 0 && (
                    <p className="text-[10px] mt-2" style={{ color: C.muted }}>
                      {stats.spend.tts_characters.toLocaleString()} characters of voice rendered
                      {stats.spend.chapters_with_audio?.length > 0
                        ? ` across ${stats.spend.chapters_with_audio.length} chapter${stats.spend.chapters_with_audio.length !== 1 ? 's' : ''}.`
                        : '.'}
                      {' '}Re-listening uses the cached audio file at no extra cost.
                    </p>
                  )}
                </div>
              )}

              {/* Chapter Progress with Q&A */}
              <div>
                <h4 className="text-xs font-bold uppercase mb-2" style={{ color: C.muted }}>Chapter Progress</h4>
                <div className="space-y-2">
                  {stats.chapters.map((ch) => (
                    <div key={ch.chapter_number} className="rounded-xl overflow-hidden" style={{ border: `1px solid ${ch.is_completed ? 'rgba(52,211,153,0.2)' : 'rgba(255,255,255,0.06)'}` }}>
                      {/* Chapter header */}
                      <div className="flex items-center gap-3 p-3" style={{ background: ch.is_completed ? 'rgba(52,211,153,0.08)' : 'rgba(255,255,255,0.03)' }}>
                        <div className="flex-shrink-0">
                          {ch.is_completed ? (
                            <CheckCircle size={18} style={{ color: '#34D399' }} />
                          ) : (
                            <Circle size={18} style={{ color: 'rgba(255,255,255,0.15)' }} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold truncate" style={{ color: C.cream }}>Ch. {ch.chapter_number}: {ch.title}</p>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                            <span className="text-[11px]" style={{ color: C.muted }}><Clock size={10} className="inline mr-0.5" />{formatStatsTime(ch.reading_time_seconds)}</span>
                            <span className="text-[11px]" style={{ color: C.muted }}>{ch.average_wpm || '—'} WPM</span>
                            <span className="text-[11px]" style={{ color: C.muted }}><Brain size={10} className="inline mr-0.5" />{ch.tokens_encountered.length} vocab</span>
                            <span className="text-[11px]" style={{ color: ch.vision_check_passed ? '#34D399' : C.muted }}><Eye size={10} className="inline mr-0.5" />{ch.vision_check_passed ? 'Passed' : '—'}</span>
                            {ch.times_read > 1 && <span className="text-[11px] font-bold" style={{ color: '#818CF8' }}>Read {ch.times_read}x</span>}
                          </div>
                        </div>
                      </div>

                      {/* Comprehension answers for this chapter */}
                      {ch.written_answers && ch.written_answers.length > 0 && (
                        <div className="px-3 pb-3 pt-2 ml-8" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <p className="text-[10px] font-bold uppercase mb-2" style={{ color: C.muted }}>My Answers</p>
                          {ch.written_answers.map((wa, idx) => (
                            <div key={idx} className="p-2.5 rounded-lg mb-2" style={{ background: wa.passed ? 'rgba(52,211,153,0.06)' : 'rgba(239,68,68,0.06)', borderLeft: `3px solid ${wa.passed ? '#34D399' : '#EF4444'}` }}>
                              <p className="text-xs font-bold mb-1" style={{ color: C.cream }}>Q: {wa.question}</p>
                              <p className="text-xs mb-1" style={{ color: C.muted }}>A: <span className="italic" style={{ color: C.cream }}>"{wa.student_answer}"</span></p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded" style={{ background: wa.passed ? 'rgba(52,211,153,0.2)' : 'rgba(239,68,68,0.2)', color: wa.passed ? '#34D399' : '#EF4444' }}>
                                  {wa.passed ? 'PASSED' : 'TRY AGAIN'}
                                </span>
                                {wa.comprehension_score > 0 && (
                                  <span className="text-[10px] font-bold" style={{ color: C.muted }}>Score: {wa.comprehension_score}/100</span>
                                )}
                              </div>
                              {wa.feedback && <p className="text-[11px] italic mt-1" style={{ color: C.muted }}>{wa.feedback}</p>}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Spelling errors for this chapter */}
                      {ch.spelling_errors && ch.spelling_errors.length > 0 && (
                        <div className="px-3 pb-3 pt-1 ml-8" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <p className="text-[10px] font-bold uppercase mb-1.5" style={{ color: C.muted }}>Spelling to Practice</p>
                          <div className="flex flex-wrap gap-1.5">
                            {ch.spelling_errors.map((err, idx) => (
                              <span key={idx} className="text-xs px-2 py-1 rounded-lg" style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)' }}>
                                <span style={{ color: '#EF4444', textDecoration: 'line-through' }}>{err.written}</span>
                                <span style={{ color: C.muted }}> → </span>
                                <span className="font-bold" style={{ color: '#34D399' }}>{err.correct}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Vocabulary Assessment Results */}
              {stats.assessments.history.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase mb-2" style={{ color: C.muted }}>Vocabulary Assessment Results</h4>
                  {stats.assessments.history.map((assess, aIdx) => (
                    <div key={aIdx} className="rounded-xl overflow-hidden mb-3" style={{ border: '1px solid rgba(129,140,248,0.2)' }}>
                      <div className="flex items-center justify-between p-3" style={{ background: 'rgba(129,140,248,0.08)' }}>
                        <div>
                          <p className="text-sm font-bold" style={{ color: C.cream }}>Assessment #{stats.assessments.history.length - aIdx}</p>
                          <p className="text-[11px]" style={{ color: C.muted }}>{assess.date ? new Date(assess.date).toLocaleDateString() : ''}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold" style={{ color: '#818CF8' }}>{assess.accuracy}%</p>
                          <p className="text-[11px]" style={{ color: C.muted }}>{assess.correct}/{assess.total} correct</p>
                        </div>
                      </div>
                      {assess.questions && assess.questions.length > 0 && (
                        <div className="p-3 space-y-2">
                          {assess.questions.map((q, qIdx) => (
                            <div key={qIdx} className="p-2.5 rounded-lg" style={{ background: q.is_correct ? 'rgba(52,211,153,0.06)' : 'rgba(239,68,68,0.06)', borderLeft: `3px solid ${q.is_correct ? '#34D399' : '#EF4444'}` }}>
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-xs font-bold" style={{ color: C.cream }}>{q.word}</p>
                                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded flex-shrink-0" style={{ background: q.is_correct ? 'rgba(52,211,153,0.2)' : 'rgba(239,68,68,0.2)', color: q.is_correct ? '#34D399' : '#EF4444' }}>
                                  {q.is_correct ? 'CORRECT' : 'INCORRECT'}
                                </span>
                              </div>
                              {q.student_definition && (
                                <p className="text-xs mt-1" style={{ color: C.muted }}>Definition: <span className="italic" style={{ color: C.cream }}>"{q.student_definition}"</span></p>
                              )}
                              {q.student_sentence && (
                                <p className="text-xs mt-0.5" style={{ color: C.muted }}>Sentence: <span className="italic" style={{ color: C.cream }}>"{q.student_sentence}"</span></p>
                              )}
                              {q.feedback && <p className="text-[11px] italic mt-1" style={{ color: C.muted }}>{q.feedback}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Mastered Words */}
              {stats.vocabulary.tokens_mastered.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase mb-2" style={{ color: C.muted }}>Words Mastered</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {stats.vocabulary.tokens_mastered.map((word, idx) => (
                      <span key={idx} className="px-2.5 py-1 rounded-lg text-xs font-bold" style={{ background: 'rgba(52,211,153,0.15)', color: '#34D399', border: '1px solid rgba(52,211,153,0.25)' }}>
                        {word}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-center py-8" style={{ color: C.muted }}>No stats available yet. Start reading to see your progress!</p>
          )}
        </div>
      </div>
    </div>
  );
};

const StudentAcademy = () => {
  const { t } = useTranslation();
  const { student, studentLogout, logActivity } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [showStoryDialog, setShowStoryDialog] = useState(false);
  const [selectedNarrative, setSelectedNarrative] = useState(null);
  const [sessionCode, setSessionCode] = useState('');
  const [joiningSession, setJoiningSession] = useState(false);
  const [joinedSession, setJoinedSession] = useState(null);
  const [wizardKey, setWizardKey] = useState(0);
  const [showFAQ, setShowFAQ] = useState(false);
  const [showOffline, setShowOffline] = useState(false);
  const [showSpelling, setShowSpelling] = useState(false);
  const [showMusic, setShowMusic] = useState(false);
  const [showStatsForId, setShowStatsForId] = useState(null);
  const [showReadingBee, setShowReadingBee] = useState(false);
  const [showBedtime, setShowBedtime] = useState(false);

  useEffect(() => {
    if (student?.id) {
      logActivity('page_view', '/academy', { student_id: student.id });
    }
  }, [student?.id, logActivity]);

  const resetOnboarding = () => {
    localStorage.removeItem(`sv_onboarding_student_${student?.id || student?.student_code}`);
    setWizardKey((k) => k + 1);
  };

  const { data: studentData, isLoading: studentLoading } = useQuery({
    queryKey: ['student-detail', student?.id],
    queryFn: async () => (await studentAPI.getById(student?.id)).data,
    enabled: !!student?.id,
  });

  const { data: narratives = [] } = useQuery({
    // Always fetch with archived included; we split the list client-side
    // into recent vs Archive section. The backend already enforces the
    // keep-last-three rule and returns everything sorted by created_date desc.
    queryKey: ['student-narratives', student?.id],
    queryFn: async () => (await narrativeAPI.getAll(student?.id, { includeArchived: true })).data,
    enabled: !!student?.id,
  });

  const sortedNarratives = React.useMemo(() => {
    return [...(narratives || [])].sort((a, b) => {
      const ta = a?.created_date ? new Date(a.created_date).getTime() : 0;
      const tb = b?.created_date ? new Date(b.created_date).getTime() : 0;
      return tb - ta;
    });
  }, [narratives]);
  const recentNarratives = sortedNarratives.filter((n) => n.status !== 'archived');
  const archivedNarratives = sortedNarratives.filter((n) => n.status === 'archived');

  // Archive / unarchive / delete a story from the student's library.
  const invalidateNarratives = () => queryClient.invalidateQueries(['student-narratives', student?.id]);

  const archiveMut = useMutation({
    mutationFn: (id) => narrativeAPI.archive(id),
    onSuccess: () => { invalidateNarratives(); toast.success('Story archived'); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to archive'),
  });

  const unarchiveMut = useMutation({
    mutationFn: (id) => narrativeAPI.unarchive(id),
    onSuccess: () => { invalidateNarratives(); toast.success('Story restored'); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to restore'),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => narrativeAPI.delete(id),
    onSuccess: () => { invalidateNarratives(); toast.success('Story deleted'); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to delete'),
  });

  const { data: goalProgress } = useQuery({
    queryKey: ['student-reading-goal', student?.id],
    queryFn: async () => { try { return (await studentAPI.getReadingGoalProgress(student?.id)).data; } catch { return { has_goal: false }; } },
    enabled: !!student?.id,
  });

  const handleLogout = () => { studentLogout(); navigate('/student-login'); };

  // Poll live info for the joined session
  const { data: liveInfo } = useQuery({
    queryKey: ['live-info', joinedSession?.session_id],
    queryFn: async () => (await classroomAPI.liveInfo(joinedSession.session_id)).data,
    enabled: !!joinedSession?.session_id,
    refetchInterval: 10000, // poll every 10s
  });

  const handleJoinSession = async (e) => {
    e.preventDefault();
    if (!sessionCode.trim() || !student?.id) return;
    setJoiningSession(true);
    try {
      const res = await classroomAPI.join(sessionCode.trim(), student.id);
      setJoinedSession(res.data);
      toast.success(`Joined session: ${res.data.title}`);
      setSessionCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not join session.');
    }
    setJoiningSession(false);
  };

  if (studentLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
        <div className="text-xl font-bold" style={{ color: C.cream }}>{t('student.loading')}</div>
      </div>
    );
  }

  if (selectedNarrative) {
    return <NarrativeReader narrative={selectedNarrative} student={studentData} onClose={() => setSelectedNarrative(null)} />;
  }

  // Music-only mode when classroom is locked
  if (studentData?.classroom_locked) {
    return (
      <AppShell title="My Music" subtitle={`${t('common.welcome')}, ${studentData?.full_name}!`} onLogout={handleLogout}
        isStudent={true} studentId={student?.id}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 p-4 rounded-xl mb-6" style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)' }}>
            <Music size={20} style={{ color: '#818CF8' }} />
            <div>
              <p className="text-sm font-bold" style={{ color: '#818CF8' }}>Music Mode</p>
              <p className="text-xs" style={{ color: C.muted }}>Your classroom is currently paused. Enjoy your music library!</p>
            </div>
          </div>
          <StudentMediaLibrary student={studentData} />
        </div>
      </AppShell>
    );
  }

  const canGenerateStory = studentData?.assigned_banks?.length > 0;
  const masteredCount = studentData?.mastered_tokens?.length || 0;
  const biologicalTarget = studentData?.biological_target || 1000;
  const agenticScore = studentData?.agentic_reach_score || 0;

  return (
    <AppShell title={t('student.academy')} subtitle={`${t('common.welcome')}, ${studentData?.full_name}!`} onLogout={handleLogout}
      isStudent={true} studentId={student?.id}
      rightContent={
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => { setShowSpelling(!showSpelling); setShowOffline(false); setShowFAQ(false); setShowMusic(false); setShowReadingBee(false); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:scale-105"
            style={{ color: showSpelling ? '#D4A853' : '#94A3B8', border: '1px solid rgba(255,255,255,0.1)', background: showSpelling ? 'rgba(212,168,83,0.1)' : 'rgba(255,255,255,0.04)' }}
            data-testid="student-spelling-btn">
            <Award size={14} /> {t('student.spellingBee')}
          </button>
          <button onClick={() => { setShowReadingBee(!showReadingBee); setShowSpelling(false); setShowOffline(false); setShowFAQ(false); setShowMusic(false); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:scale-105"
            style={{ color: showReadingBee ? '#D4A853' : '#94A3B8', border: '1px solid rgba(255,255,255,0.1)', background: showReadingBee ? 'rgba(212,168,83,0.1)' : 'rgba(255,255,255,0.04)' }}
            data-testid="student-reading-bee-btn">
            <Trophy size={14} /> Reading Bee
          </button>
          <button onClick={() => { setShowBedtime(!showBedtime); setShowSpelling(false); setShowReadingBee(false); setShowOffline(false); setShowFAQ(false); setShowMusic(false); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:scale-105"
            style={{ color: showBedtime ? '#D4A853' : '#94A3B8', border: '1px solid rgba(255,255,255,0.1)', background: showBedtime ? 'rgba(212,168,83,0.1)' : 'rgba(255,255,255,0.04)' }}
            data-testid="student-bedtime-btn">
            <Moon size={14} /> Bedtime Stories
          </button>
          <button onClick={() => navigate('/academy/my-music')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:scale-105"
            style={{ color: '#94A3B8', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)' }}
            data-testid="student-music-btn">
            <Music size={14} /> {t('student.myMusic')}
          </button>
          <button onClick={() => { setShowOffline(!showOffline); setShowSpelling(false); setShowFAQ(false); setShowMusic(false); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:scale-105"
            style={{ color: showOffline ? '#D4A853' : '#94A3B8', border: '1px solid rgba(255,255,255,0.1)', background: showOffline ? 'rgba(212,168,83,0.1)' : 'rgba(255,255,255,0.04)' }}
            data-testid="student-offline-btn">
            <WifiOff size={14} /> {t('student.offline')}
          </button>
          <button onClick={() => { setShowFAQ(!showFAQ); setShowOffline(false); setShowSpelling(false); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:scale-105"
            style={{ color: showFAQ ? '#D4A853' : '#94A3B8', border: '1px solid rgba(255,255,255,0.1)', background: showFAQ ? 'rgba(212,168,83,0.1)' : 'rgba(255,255,255,0.04)' }}
            data-testid="student-faq-btn">
            <HelpCircle size={14} /> {t('student.faq')}
          </button>
          <button onClick={resetOnboarding}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:scale-105"
            style={{ color: '#94A3B8', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)' }}
            data-testid="reset-onboarding-btn">
            <RotateCcw size={14} /> {t('student.tutorial')}
          </button>
        </div>
      }
    >
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Reading Bee Banner — prominent announcement of active Bees */}
        <ReadingBeeBanner
          onOpen={() => {
            setShowReadingBee(true);
            setShowSpelling(false); setShowOffline(false);
            setShowFAQ(false); setShowMusic(false);
          }}
        />

        {/* FAQ Section */}
        {showFAQ && (
          <div className="mb-6">
            <FAQSection items={studentFAQ} title={t('student.studentFaq')} />
          </div>
        )}

        {/* Offline Library Section */}
        {showOffline && (
          <div className="mb-6">
            <h2 className="text-lg sm:text-xl font-bold mb-3" style={{ fontFamily: "'Sora', sans-serif", color: C.cream }}>{t('student.offlineLibrary')}</h2>
            <OfflineLibrary studentId={student?.id} onReadStory={(story) => setSelectedNarrative(story)} />
          </div>
        )}

        {/* Spelling Bee Section */}
        {showSpelling && (
          <div className="mb-6">
            <h2 className="text-lg sm:text-xl font-bold mb-3" style={{ fontFamily: "'Sora', sans-serif", color: C.cream }}>{t('student.spellingBeeContests')}</h2>
            <SpellingBee studentId={student?.id} studentName={studentData?.full_name} />
          </div>
        )}

        {/* Bedtime Stories Section */}
        {showBedtime && (
          <div className="mb-6">
            <StudentBedtimeShelf studentName={studentData?.full_name} />
          </div>
        )}

        {/* Reading Bee Section */}
        {showReadingBee && (
          <div className="mb-6">
            <StudentReadingBee
              studentId={student?.id}
              studentName={studentData?.full_name}
              onOpenStory={async (beeId) => {
                try {
                  // Find-or-clone the Bee's story into this student's library,
                  // then open it in the reader. Handles the case where the
                  // Bee was created pointing at another student's narrative.
                  const res = await readingBeeAPI.openStory(beeId, student?.id);
                  const opened = res.data?.narrative;
                  if (res.data?.cloned) {
                    toast.success('Story added to your library!');
                    queryClient.invalidateQueries(['narratives']);
                  }
                  if (opened) {
                    setShowReadingBee(false);
                    setSelectedNarrative(opened);
                  } else {
                    toast.error('Could not open the story.');
                  }
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Could not open the story.');
                }
              }}
            />
          </div>
        )}

        {/* Task Reminders - always visible */}
        <TaskReminders
          studentId={student?.id}
          onOpenStory={(narrativeId) => {
            const n = narratives.find(n => n.id === narrativeId);
            if (n) setSelectedNarrative(n);
          }}
          onOpenSpelling={() => { setShowSpelling(true); setShowOffline(false); setShowFAQ(false); }}
        />

        {/* Reading Goal Banner */}
        {goalProgress?.has_goal && (
          <div className="p-4 rounded-2xl mb-4" style={{
            background: goalProgress.is_on_track ? 'rgba(52,211,153,0.08)' : 'rgba(239,68,68,0.08)',
            border: `2px solid ${goalProgress.is_on_track ? 'rgba(52,211,153,0.3)' : 'rgba(239,68,68,0.3)'}`,
          }}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Target size={18} style={{ color: goalProgress.is_on_track ? '#34D399' : '#EF4444' }} />
                <span className="text-sm font-bold" style={{ color: C.cream }}>
                  Reading Goal: {goalProgress.stories_completed}/{goalProgress.stories_required} stories {goalProgress.period_label}
                </span>
              </div>
              <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full" style={{
                background: goalProgress.is_on_track ? 'rgba(52,211,153,0.2)' : 'rgba(239,68,68,0.2)',
                color: goalProgress.is_on_track ? '#34D399' : '#EF4444',
              }}>
                {goalProgress.is_on_track ? 'On Track!' : `${goalProgress.remaining} more to go!`}
              </span>
            </div>
            <div className="w-full h-2.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <div className="h-full rounded-full transition-all" style={{
                width: `${goalProgress.completion_percentage}%`,
                background: goalProgress.is_on_track ? '#34D399' : `linear-gradient(90deg, #EF4444, #F59E0B)`,
              }} />
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <StatCard icon={BookMarked} label={t('student.vocabMastered')} value={masteredCount} sub={`${t('student.target')}: ${biologicalTarget} words`} accent="#818CF8" />
          <StatCard icon={TrendingUp} label="Agentic Reach Score" value={Math.round(agenticScore)} sub={agenticScore >= 600 ? 'Expert' : agenticScore >= 300 ? 'Adept' : agenticScore >= 100 ? 'Apprentice' : 'Initiate'} accent="#34D399" />
          <StatCard icon={Clock} label={t('student.readingTime')} value={`${Math.floor((studentData?.total_reading_seconds || 0) / 60)}m`} sub={`${studentData?.average_wpm || 0} WPM ${t('student.average')}`} accent="#FBBF24" />
        </div>

        {/* Writing Studio - always visible: the child writes here, and the jobs grow with them */}
        <WritingStudio />

        {/* Join Classroom */}
        <div className="p-4 sm:p-5 rounded-2xl mb-4 sm:mb-6" style={{ background: C.card, border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56,189,248,0.12)' }}>
                <Users size={20} style={{ color: C.teal }} />
              </div>
              <div>
                <h3 className="text-sm sm:text-base font-bold" style={{ color: C.cream }}>{t('student.joinClassroom')}</h3>
                <p className="text-xs" style={{ color: C.muted }}>{t('student.enterTeacherCode')}</p>
              </div>
            </div>
            <form onSubmit={handleJoinSession} className="flex items-center gap-2 sm:ml-auto">
              <input type="text" value={sessionCode} onChange={(e) => setSessionCode(e.target.value)}
                placeholder="6-digit code" maxLength={6}
                className="px-3 sm:px-4 py-2 rounded-xl font-mono font-bold text-center w-28 sm:w-36 text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: C.cream }}
                data-testid="session-code-input" />
              <button type="submit" disabled={joiningSession || sessionCode.length < 6}
                className="px-4 sm:px-5 py-2 rounded-xl text-sm font-bold text-black disabled:opacity-50"
                style={{ background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` }}
                data-testid="join-session-btn">
                {joiningSession ? '...' : 'Join'}
              </button>
            </form>
          </div>
          {joinedSession && (
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }} data-testid="joined-session-info">
              <p className="text-sm font-bold" style={{ color: '#34D399' }}>Joined: {joinedSession.title}</p>
              {liveInfo?.is_live && liveInfo?.room_url && (
                <button
                  onClick={() => window.open(liveInfo.room_url, '_blank')}
                  className="mt-3 flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white transition-all hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #DC2626, #EF4444)' }}
                  data-testid="join-live-class-btn"
                >
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-300 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-100"></span>
                  </span>
                  <Video size={18} /> JOIN LIVE CLASS
                </button>
              )}
            </div>
          )}
        </div>

        {/* Stories */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4 gap-2">
            <h2 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif", color: C.cream }}>{t('student.yourStories')}</h2>
            <button onClick={() => setShowStoryDialog(true)} disabled={!canGenerateStory}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold text-black transition-all hover:scale-105 disabled:opacity-50 flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` }}>
              <Plus size={16} /> <span className="hidden sm:inline">{t('student.createNewStory')}</span><span className="sm:hidden">New</span>
            </button>
          </div>

          {!canGenerateStory && (
            <div className="p-3 sm:p-4 rounded-xl mb-4" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}>
              <p className="text-xs sm:text-sm font-semibold" style={{ color: '#FBBF24' }}>
                No word banks assigned! Ask your guardian to assign word banks.
              </p>
            </div>
          )}

          {(() => {
            const renderCard = (narrative) => {
              const chaptersCompleted = narrative.chapters_completed?.length || 0;
              const isCompleted = narrative.status === 'completed';
              const isArchived = narrative.status === 'archived';
              return (
                <div key={narrative.id}
                  className="p-4 sm:p-5 rounded-2xl cursor-pointer transition-all hover:scale-[1.02]"
                  style={{ background: C.card, border: '1px solid rgba(255,255,255,0.08)', opacity: isArchived ? 0.85 : 1 }}
                  onClick={() => setSelectedNarrative(narrative)}>
                  <h3 className="text-sm sm:text-base font-bold mb-1" style={{ color: C.cream }}>{narrative.title}</h3>
                  <p className="text-xs mb-1 line-clamp-2" style={{ color: C.muted }}>{narrative.theme}</p>
                  {narrative.created_date && (
                    <p className="text-xs mb-3 sm:mb-4" style={{ color: C.muted }}>
                      Created: {new Date(narrative.created_date).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}
                    </p>
                  )}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1" style={{ color: C.muted }}>
                      <span>Progress</span>
                      <span>{chaptersCompleted}/5</span>
                    </div>
                    <div className="w-full h-2 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
                      <div className="h-2 rounded-full transition-all" style={{ width: `${(chaptersCompleted / 5) * 100}%`, background: isCompleted ? '#34D399' : `linear-gradient(90deg, ${C.gold}, ${C.teal})` }} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold px-2 sm:px-3 py-1 rounded-full"
                      style={{ background: isCompleted ? 'rgba(52,211,153,0.15)' : 'rgba(245,158,11,0.15)', color: isCompleted ? '#34D399' : '#FBBF24' }}>
                      {isCompleted ? 'Completed' : 'In Progress'}
                    </span>
                    <span className="text-xs" style={{ color: C.muted }}>{narrative.total_word_count} words</span>
                  </div>
                  <div className="flex gap-2 mt-3 sm:mt-4">
                    <button onClick={(e) => { e.stopPropagation(); setSelectedNarrative(narrative); }}
                      className="flex-1 py-2 rounded-xl text-sm font-bold text-black transition-all hover:scale-[1.02]"
                      style={{ background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` }}>
                      {isCompleted ? 'Read Again' : 'Continue Reading'} <ArrowRight size={14} className="inline ml-1" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setShowStatsForId(narrative.id); }}
                      className="px-3 py-2 rounded-xl text-sm font-bold transition-all hover:scale-[1.02] flex items-center gap-1"
                      style={{ background: 'rgba(129,140,248,0.15)', color: '#818CF8', border: '1px solid rgba(129,140,248,0.3)' }}
                      data-testid={`story-stats-btn-${narrative.id}`}>
                      <BarChart3 size={14} /> Stats
                    </button>
                  </div>
                  <div className="flex gap-2 mt-2">
                    {isArchived ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); unarchiveMut.mutate(narrative.id); }}
                        className="flex-1 py-1.5 rounded-lg text-xs font-bold transition-all hover:scale-[1.02] flex items-center justify-center gap-1"
                        style={{ background: 'rgba(52,211,153,0.12)', color: '#34D399', border: '1px solid rgba(52,211,153,0.3)' }}
                      >
                        <RefreshCw size={12} /> Restore
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); archiveMut.mutate(narrative.id); }}
                        className="flex-1 py-1.5 rounded-lg text-xs font-bold transition-all hover:scale-[1.02] flex items-center justify-center gap-1"
                        style={{ background: 'rgba(148,163,184,0.12)', color: C.muted, border: '1px solid rgba(148,163,184,0.25)' }}
                      >
                        <Archive size={12} /> Archive
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Delete "${narrative.title}"? This cannot be undone.`)) {
                          deleteMut.mutate(narrative.id);
                        }
                      }}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all hover:scale-[1.02] flex items-center justify-center gap-1"
                      style={{ background: 'rgba(244,63,94,0.1)', color: '#F87171', border: '1px solid rgba(244,63,94,0.3)' }}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              );
            };

            if (sortedNarratives.length === 0) {
              return (
                <div className="text-center py-12 sm:py-16 rounded-2xl" style={{ background: C.card, border: '1px solid rgba(255,255,255,0.08)' }}>
                  <BookOpen size={40} className="mx-auto mb-4" style={{ color: C.gold }} />
                  <p className="text-lg sm:text-xl font-bold mb-2" style={{ color: C.cream }}>No stories yet</p>
                  <p className="text-xs sm:text-sm mb-6" style={{ color: C.muted }}>Create your first AI-generated story and start learning!</p>
                  {canGenerateStory && (
                    <button onClick={() => setShowStoryDialog(true)}
                      className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-black mx-auto hover:scale-105 transition-all"
                      style={{ background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` }}>
                      <Plus size={18} /> {t('student.createFirstStory')}
                    </button>
                  )}
                </div>
              );
            }

            return (
              <>
                {recentNarratives.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {recentNarratives.map(renderCard)}
                  </div>
                )}

                {archivedNarratives.length > 0 && (
                  <div className="mt-6">
                    <button
                      onClick={() => setShowArchived((v) => !v)}
                      className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-left transition-all hover:scale-[1.005]"
                      style={{ background: C.card, border: '1px solid rgba(255,255,255,0.08)' }}
                      data-testid="toggle-archived-stories"
                      aria-expanded={showArchived}
                    >
                      <span className="flex items-center gap-2 text-sm font-bold uppercase" style={{ color: C.cream }}>
                        <Archive size={16} style={{ color: C.gold }} />
                        Archive
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(212,168,83,0.15)', color: C.gold }}>
                          {archivedNarratives.length}
                        </span>
                      </span>
                      <span className="text-xs font-bold" style={{ color: C.muted }}>
                        {showArchived ? '▲ Hide' : '▼ Show'}
                      </span>
                    </button>
                    {showArchived && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-3" data-testid="archived-stories-grid">
                        {archivedNarratives.map(renderCard)}
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {showStoryDialog && (
        <StoryGenerationDialog isOpen={showStoryDialog} onClose={() => setShowStoryDialog(false)} student={studentData} />
      )}

      {showStatsForId && (
        <StoryStatsModal narrativeId={showStatsForId} onClose={() => setShowStatsForId(null)} />
      )}

      <OnboardingWizard
        key={wizardKey}
        steps={studentOnboardingSteps}
        portalType="student"
        userId={student?.id || student?.student_code}
      />
    </AppShell>
  );
};

export default StudentAcademy;
